// @flow
import {
  TimeUtil,
  DateUtil,
  TimestampUtil,
  DateTimeUtil,
} from "../../../utils/datetime/DateTimeUtil";

import type { IConstraintModel } from "../../types";
import {
  IllegalArgumentException,
  IllegalStateException,
} from "../../../exceptions";
import { DATETIME_OFFSET_FORMAT } from "../../../constants";

const INEXACT_DATE_ID = "Constraint.Date.InexactDate";
const OUT_OF_RANGE_ID = "Constraint.Date.OutOfRange";
const BELOW_MINIMUM_ID = "Constraint.Date.BelowMinimum";
const ABOVE_MAXIMUM_ID = "Constraint.Date.AboveMaximum";

/**
 */
class DateBoundaryConstraint implements IConstraintModel {
  _type: string;
  _format: string;
  _formatNoOffset: string;
  _minDate: ?string;
  _maxDate: ?string;

  /**
   */
  constructor(
    type: string,
    minDate: ?string = null,
    maxDate: ?string = null,
    format: string = "yyyy-MM-dd",
  ) {
    this._type = type;
    this._format = format;
    this._formatNoOffset = format.replace(DATETIME_OFFSET_FORMAT, "").trim();
    this._minDate = minDate;
    this._maxDate = maxDate;
  }

  /**
   */
  get type(): string {
    return this._type;
  }

  /**
   */
  get id(): string {
    if (
      this.minDate !== null &&
      this.maxDate !== null &&
      this.minDate === this.maxDate
    ) {
      return INEXACT_DATE_ID;
    } else if (this.minDate !== null && this.maxDate !== null) {
      return OUT_OF_RANGE_ID;
    } else if (this.minDate !== null) {
      return BELOW_MINIMUM_ID;
    } else if (this.maxDate !== null) {
      return ABOVE_MAXIMUM_ID;
    }

    return "Constraint.Date.NoBoundaryConstraint";
  }

  /**
   */
  get format(): string {
    return this._format;
  }

  /**
   */
  get minDate(): ?string {
    return this._minDate;
  }

  /**
   */
  get maxDate(): ?string {
    return this._maxDate;
  }

  /**
   */
  get defaultMessage(): string {
    switch (this.id) {
      case INEXACT_DATE_ID:
        return "Date must be precisely ${min-date}"; // NOSONAR
      case OUT_OF_RANGE_ID:
        return "Must be between ${min-date} and ${max-date}"; // NOSONAR
      case BELOW_MINIMUM_ID:
        return "Must be on or after ${min-date}"; // NOSONAR
      case ABOVE_MAXIMUM_ID:
        return "Must be on or before ${max-date}"; // NOSONAR
      default:
        return "";
    }
  }

  /**
   */
  get parameters(): { "max-date": string | null, "min-date": string | null } {
    return {
      "min-date": this.minDate
        ? this.formatUtil.toFormat(this.minDate, this._formatNoOffset)
        : null,
      "max-date": this.maxDate
        ? this.formatUtil.toFormat(this.maxDate, this._formatNoOffset)
        : null,
    };
  }

  /**
   */
  hasValidation(): boolean {
    return true;
  }

  /**
   */
  isExactDate(value: string): boolean {
    if (typeof value !== "string") {
      throw new IllegalArgumentException(
        "isBetweenDates: Not comparing to string",
      );
    }

    if (this.minDate === null) {
      throw new IllegalStateException("isExactDate: Min and max date not set");
    }

    return this.formatUtil.isSame(value, this.minDate, this.format);
  }

  /**
   */
  isBetweenDates(value: string): boolean {
    if (typeof value !== "string") {
      throw new IllegalArgumentException(
        "isBetweenDates: Not comparing to string",
      );
    }

    if (this.minDate === null || this.maxDate === null) {
      throw new IllegalStateException("isBetweenDate: Min or max date not set");
    }

    return (
      this.isSameOrAfterMinDate(value) && this.isSameOrBeforeMaxDate(value)
    );
  }

  /**
   */
  isSameOrAfterMinDate(value: string): boolean {
    if (typeof value !== "string") {
      throw new IllegalArgumentException(
        "isSameOrAfterMinDate: Not comparing to string",
      );
    }

    if (this.minDate == null) {
      throw new IllegalStateException("isSameOrAfterMinDate: Min date not set");
    }

    return this.formatUtil.isSameOrAfter(value, this.minDate, this.format);
  }

  /**
   */
  isSameOrBeforeMaxDate(value: string): boolean {
    if (typeof value !== "string") {
      throw new IllegalArgumentException(
        "isSameOrBeforeMaxDate: Not comparing to string",
      );
    }

    if (this.maxDate == null) {
      throw new IllegalStateException(
        "isSameOrBeforeMaxDate: Max date not set",
      );
    }

    return this.formatUtil.isSameOrBefore(value, this.maxDate, this.format);
  }

  /**
   */
  get formatUtil():
    | typeof TimeUtil
    | typeof TimestampUtil
    | typeof DateTimeUtil
    | typeof DateUtil {
    switch (this.type) {
      case "time":
        return TimeUtil;
      case "timestamp":
        return TimestampUtil;
      case "datetime":
        return DateTimeUtil;
      default:
        return DateUtil;
    }
  }

  /**
   */
  validate(value: string): boolean {
    if (typeof value !== "string") {
      throw new TypeError("isNotEmptyString: Not comparing to string");
    }

    switch (this.id) {
      case INEXACT_DATE_ID:
        return this.isExactDate(value);
      case OUT_OF_RANGE_ID:
        return this.isBetweenDates(value);
      case BELOW_MINIMUM_ID:
        return this.isSameOrAfterMinDate(value);
      case ABOVE_MAXIMUM_ID:
        return this.isSameOrBeforeMaxDate(value);
      default:
        return true;
    }
  }

  /**
   */
  get isMandatoryConstraint(): boolean {
    return false;
  }
}

export default DateBoundaryConstraint;
